import { Fragment, useState, useEffect, useRef } from 'react'
import { useRecoilState } from 'recoil'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import axios from '../../plugins/axios'
import { checkEmptyValue } from '../../plugins/common';
import { selectBedInfoState, stompClientState, playerBedDataState } from '../../plugins/recoil'

import Dropdown from './Dropdown'
import PatientPlayer from './PatientPlayer'

import { ReactComponent as IconPlay } from '../../assets/icons/ic-play-28.svg';
import { ReactComponent as IconPause } from '../../assets/icons/ic-pause-28.svg';
import { ReactComponent as IconSetting } from '../../assets/icons/ic-setting.svg';
import { ReactComponent as IconCloseBlack } from '../../assets/icons/ic-close-black.svg';

const ReviewPlayer = ({ selectedAlarmInfo, handleClose }) => {
    const { t } = useTranslation()
    const [curSec, setCurSec] = useState(0)
    const [speed, setSpeed] = useState(25)
    const [editSpeed, setEditSpeed] = useState(25)
    const [totalSec, setTotalSec] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [showSetting, setShowSetting] = useState(false)
    const [dropdownStates, setDropdownStates] = useState({
        wave1: false, wave2: false, wave3: false,
        wave4: false, wave5: false, wave6: false,
        numeric1: false, numeric2: false, numeric3: false,
        numeric4: false, numeric5: false, numeric6: false,
        numeric7: false, numeric8: false, numeric9: false,
        numeric10: false, numeric11: false, numeric12: false
    });
    const [waveCodeList, setWaveCodeList] = useState([])
    const [waveParameters, setWaveParameters] = useState([])
    const [editWaveParameters, setEditWaveParameters] = useState([])
    const [numericCodeList, setNumericCodeList] = useState([])
    const [numericParameters, setNumericParameters] = useState([])
    const [editNumericParameters, setEditNumericParameters] = useState([])
    const [bedData, setBedData] = useRecoilState(playerBedDataState)
    
    const [stompClient] = useRecoilState(stompClientState)
    const [selectBedInfo] = useRecoilState(selectBedInfoState)

    const waveSubscriptionRef = useRef(null);
    const numericSubscriptionRef = useRef(null);
    const settingSubscriptionRef = useRef(null);
    const alarmSubscriptionRef = useRef(null);

    const sessionInfoRef = useRef(null)

    const speedOptions = [
        { value: 6.25, label: '6.25 mm/s' },
        { value: 12.5, label: '12.5 mm/s' },
        { value: 25, label: '25 mm/s' },
        { value: 50, label: '50 mm/s' },
    ]

    const makeTimeString = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    const handleClickDropdown = (type, val) => {
        setDropdownStates(prev => ({
          ...prev,
          [type]: typeof val !== 'undefined' ? false : !prev[type],
        }));
    };

    const handleChangeSelect = (isWave, val, index) => {
        if (isWave) {
            const updatedWaveParameters = [...editWaveParameters];
            updatedWaveParameters[index] = {
                ...updatedWaveParameters[index],
                measureParameterId: val.id,
                measureParameter: val.parameter,
                measureParameterName: val.name
            };
            setEditWaveParameters(updatedWaveParameters);
        } else {
            const updatedNumericParameters = [...editNumericParameters];
            updatedNumericParameters[index] = {
                ...updatedNumericParameters[index],
                measureParameterId: val.id,
                measureParameter: val.parameter,
                measureParameterName: val.name
            };
            setEditNumericParameters(updatedNumericParameters);
        }
    }

    const handlePlayer = () => {
        if (isPlaying) {
            handleStompClient('pause')
        } else {
            if (curSec === 0) {
                handleStompClient('play')
            } else {
                handleStompClient('resume')
            }
        }
        setIsPlaying(!isPlaying)
    }

    const handleStompClient = async (type) => {
        if (!stompClient) return;

        // STOMP 클라이언트 연결 상태 확인
        console.log('STOMP client connected:', stompClient.connected);
    
        switch (type) {
            case 'play':
                if (waveSubscriptionRef.current) {
                    waveSubscriptionRef.current.unsubscribe();
                    waveSubscriptionRef.current = null;
                }
                if (numericSubscriptionRef.current) {
                    numericSubscriptionRef.current.unsubscribe();
                    numericSubscriptionRef.current = null;
                }
                if (alarmSubscriptionRef.current) {
                    alarmSubscriptionRef.current.unsubscribe();
                    alarmSubscriptionRef.current = null;
                }
                if (settingSubscriptionRef.current) {
                    settingSubscriptionRef.current.unsubscribe();
                    settingSubscriptionRef.current = null;
                }
                try {
                    const res = await axios.post(`${process.env.REACT_APP_SOCKET_URL}/ws/api/playback/sessions`, {
                        serial: selectedAlarmInfo.serialNo,
                        startTime: `${moment(selectedAlarmInfo.startTime).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`,
                        endTime: `${moment(selectedAlarmInfo.endTime).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`,
                    })
                    sessionInfoRef.current = res
                    const { destinations } = res
                    
                    numericSubscriptionRef.current = stompClient.subscribe(destinations.vitals, (message) => {
                        const vitalData = JSON.parse(message.body);
                        // console.log('Vital data:', vitalData);
                        setBedData(prev => ({
                            ...prev,
                            numericData: {
                                ...prev.numericData,
                                ...vitalData
                            }
                        }))
                    });

                    // ALARM 구독 (알람)
                    alarmSubscriptionRef.current = stompClient.subscribe(destinations.alarm, (message) => {
                        const alarmData = JSON.parse(message.body);
                        // console.log('Alarm:', alarmData);
                        setBedData(prev => ({
                            ...prev,
                            alarmData: alarmData.map(item => {
                                return {
                                    ...item,
                                    startTime: item.startTime
                                }
                            })
                        }))
                    });

                    // WAVE 구독 (파형)
                    waveSubscriptionRef.current = stompClient.subscribe(destinations.wave, (message) => {
                        const waveData = JSON.parse(message.body);
                        // console.log('Wave data:', waveData);
                        setBedData(prev => ({
                            ...prev,
                            waveData: waveData
                        }))
                    });

                    // SETTINGS 구독 (설정)
                    settingSubscriptionRef.current = stompClient.subscribe(destinations.settings, (message) => {
                        const settingsData = JSON.parse(message.body);
                        // console.log('Settings data:', settingsData)
                        setBedData(prev => ({
                            ...prev,
                            settingData: {
                                ...prev.settingData,
                                ...settingsData,
                            }
                        }))
                    });

                    const startRes = await axios.post(`${process.env.REACT_APP_SOCKET_URL}/ws/api/playback/sessions/${res.sessionId}/start`)
                } catch (err) {
                    console.warn(err)
                    sessionInfoRef.current = null
                }
                break;
            case 'pause':
                if (!sessionInfoRef.current) return;
                try {
                    const res = await axios.post(`${process.env.REACT_APP_SOCKET_URL}/ws/api/playback/sessions/${sessionInfoRef.current.sessionId}/pause`)
                } catch (err) {
                    console.warn(err)
                }
                break;
            case 'resume':
                if (!sessionInfoRef.current) return;
                try {
                    const res = await axios.post(`${process.env.REACT_APP_SOCKET_URL}/ws/api/playback/sessions/${sessionInfoRef.current.sessionId}/resume`)
                } catch (err) {
                    console.warn(err)
                }
                break;
        }
    }

    const getDefaultParamters = async () => {
        try {
            const res = await axios.get(`/device/default/display`)
            if (res.code != '200') throw res
            setWaveParameters(res.data.waveSettings)
            setNumericParameters(res.data.numericSettings)
            setEditWaveParameters(res.data.waveSettings)
            setEditNumericParameters(res.data.numericSettings)
        } catch (err) { console.warn(err) }
    }

    const getParameters = async () => {
        try {
            const waveCodeRes = await axios.get(`/measure/parameters/wave`);
            if (waveCodeRes.code != '200') throw waveCodeRes;            
            setWaveCodeList(waveCodeRes.data.parameters);
            
            //Numeric Code List 가져오기
            const numericCodeRes = await axios.get(`measure/parameters/numeric`);
            if (numericCodeRes.code != '200') throw numericCodeRes;            
            setNumericCodeList(numericCodeRes.data.parameters);
        } catch (err) { console.warn(err) }
    }

    const deleteSession = () => {
        if (sessionInfoRef.current?.sessionId) {
            axios.delete(`${process.env.REACT_APP_SOCKET_URL}/ws/api/playback/sessions/${sessionInfoRef.current?.sessionId}`)
        }

        if (waveSubscriptionRef.current) {
            waveSubscriptionRef.current.unsubscribe();
            waveSubscriptionRef.current = null;
        }
        if (numericSubscriptionRef.current) {
            numericSubscriptionRef.current.unsubscribe();
            numericSubscriptionRef.current = null;
        }
        if (alarmSubscriptionRef.current) {
            alarmSubscriptionRef.current.unsubscribe();
            alarmSubscriptionRef.current = null;
        }
        if (settingSubscriptionRef.current) {
            settingSubscriptionRef.current.unsubscribe();
            settingSubscriptionRef.current = null;
        }
    }

    useEffect(() => {
        if (selectedAlarmInfo) {
            const startTime = moment(selectedAlarmInfo.startTime);
            const endTime = moment(selectedAlarmInfo.endTime);
            setTotalSec(endTime.diff(startTime, 'seconds'));
            setCurSec(0);
        }
    }, [selectedAlarmInfo]);

    useEffect(() => {
        if (showSetting) {
            setCurSec(0)
            setIsPlaying(false)
            handleStompClient('pause')
            setBedData({})
            deleteSession()
        }
    }, [showSetting])

    useEffect(() => {
        if (isPlaying) {
            const interval = setInterval(() => {
                setCurSec(prev => {
                    if (prev < totalSec) {
                        return prev + 1;
                    } else {
                        clearInterval(interval);
                        deleteSession()
                        setIsPlaying(false)
                        setBedData({})
                        return 0
                    }
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [isPlaying])

    useEffect(() => {
        getDefaultParamters()
        getParameters()

        return () => {
            deleteSession()
        }
    }, [])

    return (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[604px] bg-[#DEDEDE] shadow-[0px_12px_12px_0px_#00000099] z-10" style={{ borderRadius: 8 }}>
            <div className="h-[38px] flex items-center px-5 bg-[#CECECE] text-black" style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                <div className="" style={{ fontSize: 16 }}>{t('alarmReview.alarmReviewPlayer')}</div>
                <IconCloseBlack className="ml-auto cursor-pointer" onClick={handleClose} />
            </div>
            <div className="px-5 py-5 flex flex-col text-[#1F1F1F]" style={{ height: 'calc(100% - 38px)' }}>
                {
                    showSetting ?
                    <>
                        <div className="" style={{ fontSize: 18 }}>{t('alarmReview.settings')}</div>
                        <div className="flex items-center mt-4 w-full gap-2" style={{ height: 'calc(100% - 75px)' }}>
                            <div className="w-[232px] bg-[#CECECE] h-full flex flex-col gap-4" style={{ borderRadius: 8, padding: 16 }}>
                                <div className="" style={{ fontSize: 18 }}>{t('alarmReview.wave')}</div>
                                <div className="flex flex-col gap-[6px]">
                                    {
                                        editWaveParameters.map((wave, index) => {
                                            return (
                                                <Fragment key={`wave-${index}`}>
                                                    <div className="text-[#2C2C2C] opacity-70 font-light">{t('alarmReview.wave')} {index + 1}</div>
                                                    <Dropdown
                                                        type={`wave-${index + 1}`}
                                                        isShow={dropdownStates[`wave${index + 1}`]}
                                                        value={editWaveParameters[index]?.measureParameterId}
                                                        selectedParameters={editWaveParameters}
                                                        valueArr={waveCodeList}
                                                        handleClick={(val) => { handleClickDropdown(`wave${index + 1}`, val) }}
                                                        handleValueClick={(val) => { handleChangeSelect(true, val, index) }}
                                                    />
                                                </Fragment>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                            <div className="flex-grow bg-[#CECECE] h-full flex flex-col gap-4" style={{ borderRadius: 8, padding: 16 }}>
                                <div className="" style={{ fontSize: 18 }}>{t('alarmReview.numeric')}</div>
                                <div className="flex justify-between gap-4">
                                    <div className="flex flex-col gap-[6px]">
                                        {
                                            editNumericParameters.slice(0, 6).map((numeric, index) => {
                                                return (
                                                    <Fragment key={`numeric-${index}`}>
                                                        <div className="text-[#2C2C2C] opacity-70 font-light">{t('alarmReview.numeric')} {index + 1}</div>
                                                        <Dropdown
                                                            isNumeric={true}
                                                            type={`numeric-${index + 1}`}
                                                            isShow={dropdownStates[`numeric${index + 1}`]}
                                                            value={editNumericParameters[index]?.measureParameterId}
                                                            selectedParameters={editNumericParameters}
                                                            valueArr={numericCodeList}
                                                            handleClick={(val) => { handleClickDropdown(`numeric${index + 1}`, val) }}
                                                            handleValueClick={(val) => { handleChangeSelect(false, val, index) }}
                                                        />
                                                    </Fragment>
                                                )
                                            })
                                        }
                                    </div>
                                    <div className="flex flex-col gap-[6px]">
                                        {
                                            editNumericParameters.slice(6, 12).map((numeric, index) => {
                                                return (
                                                    <Fragment key={`numeric-${index + 6}`}>
                                                        <div className="text-[#2C2C2C] opacity-70 font-light">{t('alarmReview.numeric')} {index + 7}</div>
                                                        <Dropdown
                                                            isNumeric={true}
                                                            type={`numeric-${index + 7}`}
                                                            isShow={dropdownStates[`numeric${index + 7}`]}
                                                            value={editNumericParameters[index + 6]?.measureParameterId}
                                                            selectedParameters={editNumericParameters}
                                                            valueArr={numericCodeList}
                                                            handleClick={(val) => { handleClickDropdown(`numeric${index + 7}`, val) }}
                                                            handleValueClick={(val) => { handleChangeSelect(false, val, index + 6) }}
                                                        />
                                                    </Fragment>
                                                )
                                            })
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="w-[232px] bg-[#CECECE] h-full flex flex-col gap-4" style={{ borderRadius: 8, padding: 16 }}>
                                <div className="" style={{ fontSize: 18 }}>{t('alarmReview.sweepSpeed')}</div>
                                <div className="flex flex-col gap-3">
                                    {
                                        speedOptions.map((option, index) => (
                                            <div className="flex gap-[6px]" key={`speed-option-${index}`}>
                                                <label className="custom-radio">
                                                    <input
                                                        type="radio"
                                                        checked={editSpeed === option.value}
                                                        onChange={() => setEditSpeed(option.value)}
                                                    /><span className="font-light" style={{ color: 'black', paddingLeft: 28 }}>{option.label}</span>
                                                </label>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="h-[52px] flex items-center justify-end gap-2" style={{ minHeight: 52}}>
                            <div onClick={() => { setShowSetting(false) }} className="h-[28px] w-[88px] font-light flex items-center justify-center cursor-pointer" style={{ fontSize: 16, background: 'linear-gradient(180deg, #FFFFFF 0%, #DEDEDE 100%)', borderRadius: 6, border: '1px solid', borderImageSource: 'linear-gradient(180deg, #B8B8B8 0%, #B0B0B0 100%)' }}>
                                {t('alarmReview.cancel')}
                            </div>
                            <div onClick={() => { setSpeed(editSpeed); setWaveParameters(editWaveParameters); setNumericParameters(editNumericParameters); setShowSetting(false) }} className="h-[28px] w-[88px] font-light flex items-center cursor-pointer justify-center text-white" style={{ fontSize: 16, background: 'linear-gradient(180deg, #5E5E5E 0%, #383838 100%)', borderRadius: 6, border: '1px solid #5E5E5E' }}>
                                {t('alarmReview.save')}
                            </div>
                        </div>
                    </> :
                    <>
                        <div className="flex items-center w-full">
                            <div className="w-[50%]">
                                <div className="flex items-center h-[32px]" style={{ borderBottomWidth: 1, borderBottomColor:'#cecece'}}>
                                    <div className="px-4 flex items-center h-full w-[152px] bg-[#262626] text-white" style={{ }}>{t('alarmReview.patientName')}</div>
                                    <div className="px-4 flex items-center h-full flex-grow bg-[#ffffff] text-[#444444]">{checkEmptyValue(selectBedInfo.patientName)}</div>
                                </div>
                                <div className="flex items-center h-[32px]">
                                    <div className="px-4 flex items-center h-full w-[152px] bg-[#262626] text-white" style={{ }}>{t('alarmReview.patientId')}</div>
                                    <div className="px-4 flex items-center h-full flex-grow bg-[#ffffff] text-[#444444]">{checkEmptyValue(selectBedInfo.devicePatientId)}</div>
                                </div>
                            </div>
                            <div className="w-[50%]">
                                <div className="flex items-center h-[32px]" style={{ borderBottomWidth: 1, borderBottomColor:'#cecece'}}>
                                    <div className="px-4 flex items-center h-full w-[152px] bg-[#262626] text-white" style={{ }}>{t('alarmReview.recordTime')}</div>
                                    <div className="px-4 flex items-center h-full flex-grow bg-[#ffffff] text-[#444444]">
                                        {`${moment(selectedAlarmInfo.startTime).format('YYYY-MM-DD HH:mm:ss')} ~ ${moment(selectedAlarmInfo.endTime).format('YYYY-MM-DD HH:mm:ss')}`}
                                    </div>
                                </div>
                                <div className="flex items-center h-[32px]">
                                    <div className="px-4 flex items-center h-full w-[152px] bg-[#262626] text-white" style={{ }}>{t('alarmReview.alarmMessage')}</div>
                                    <div className="px-4 flex items-center h-full flex-grow bg-[#ffffff] text-[#444444] overflow-hidden" style={{ maxWidth: 'calc(100% - 152px)' }}>
                                        <span className="truncate">{checkEmptyValue(selectedAlarmInfo.eventMessage)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex-grow flex flex-col" style={{ height: 'calc(100% - 80px)' }}>
                            <div className="flex-grow" style={{ height: 'calc(100% - 53px)' }}>
                                <PatientPlayer 
                                    bedInfo={selectBedInfo}
                                    bedData={bedData}
                                    viewType={4}
                                    waveParameters={waveParameters}
                                    numericParameters={numericParameters}
                                    speed={speed}
                                    isPlaying={isPlaying}
                                    curSec={curSec}
                                />    
                            </div>
                            <div className="relative h-[9px] w-full bg-[#000000]">
                                <div className="absolute bg-gradient-to-b from-[#3182F7] to-[#1562D1] h-full" style={{ width: `${(curSec / totalSec * 100)}%` }}/>
                            </div>
                            <div className="h-[44px] flex items-center bg-[#262626] gap-[20px]" style={{ padding: '0px 20px' }}>
                                <div className="cursor-pointer" onClick={() => { handlePlayer() }}>{ isPlaying ? <IconPause /> : <IconPlay /> }</div>
                                <div className="text-white">
                                    {`${makeTimeString(curSec)}`}
                                    <span className="text-[#ffffff] opacity-50"> / {makeTimeString(totalSec)}</span>
                                </div>
                                <IconSetting className="ml-auto cursor-pointer" onClick={() => { setEditSpeed(speed); setEditWaveParameters(waveParameters); setEditNumericParameters(numericParameters); setShowSetting(true); }}/>
                            </div>
                        </div>
                    </>
                }
            </div>
        </div>
    )
}

export default ReviewPlayer